import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ActionBanner } from "../../../common/components/functional/actionBanner/ActionBanner";
import { ActionButton } from "../../../common/components/functional/actionBanner/styled";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import { useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import { SpinnerButton } from "../../../common/components/functional/spinnerButton/SpinnerButton";
import theme from "../../../theme";
import {
  InvoiceActionBannerPagingControl,
  InvoiceActionBannerPagingControlProps,
} from "../common/InvoiceActionBannerPagingControl";

interface Props extends InvoiceActionBannerPagingControlProps {
  translation: string;
  showActionButtons: boolean;
  isSaveAllowed: boolean;
  canDelete: boolean;
  canRefresh: boolean;
  onSave: () => void;
  onAcceptAndSend: () => void;
  onDelete: () => void;
  onRefresh: () => void;
}

export const EditInvoiceActionBanner = ({
  translation,
  showActionButtons = true,
  isSaveAllowed,
  canDelete,
  canRefresh,
  onSave,
  onAcceptAndSend,
  onDelete,
  onRefresh,
  currentInvoice,
  numberOfInvoices,
  cycleInvoices,
}: Props) => {
  const { t } = useTranslation(translation);
  const { status } = useAppSelector((state) => state.invoicesDetailed);
  const isLoading = status === "pending";
  const isMultipleInvoices = numberOfInvoices > 1;

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);

  const [confirmationContent, setConfirmationContent] = useState({
    title: "",
    contentText: "",
    cancelText: "",
    confirmText: "",
    confirmAction: () => {},
  });

  const confirmDelete = () => {
    setConfirmationContent({
      title: isMultipleInvoices
        ? t("confirmInvoicesDelete")
        : t("confirmInvoiceDelete"),
      contentText: isMultipleInvoices
        ? t("confirmDeleteMultipleInvoicesInfo")
        : t("confirmDeleteInfo"),
      cancelText: t("common:cancel"),
      confirmText: isMultipleInvoices
        ? t("deleteInvoices")
        : t("deleteInvoice"),
      confirmAction: () => {
        onDelete();
        setIsConfirmationDialogOpen(false);
      },
    });
    setIsConfirmationDialogOpen(true);
  };

  const confirmAcceptAndSend = () => {
    setConfirmationContent({
      title: isMultipleInvoices
        ? t("acknowledgeAndSendInvoices")
        : t("acceptAndSendInvoice"),
      contentText: isMultipleInvoices
        ? t("confirmAcceptAndSendMultipleInfo")
        : t("confirmAcceptAndSendInfo"),
      cancelText: t("common:cancel"),
      confirmText: isMultipleInvoices
        ? t("acknowledgeAndSendInvoices")
        : t("acceptAndSend"),
      confirmAction: () => {
        onAcceptAndSend();
        setIsConfirmationDialogOpen(false);
      },
    });
    setIsConfirmationDialogOpen(true);
  };

  return (
    <ActionBanner isOpen={true}>
      {showActionButtons ? (
        <>
          <Box>
            {canDelete ? (
              <ActionButton
                variant="outlined"
                color="error"
                onClick={confirmDelete}
              >
                {isMultipleInvoices ? t("deleteInvoices") : t("common:delete")}
              </ActionButton>
            ) : null}
          </Box>
          <Box sx={{ marginLeft: "auto" }}>
            {canRefresh ? (
              <SpinnerButton
                label={t("refreshInvoiceData")}
                onClick={onRefresh}
                isLoading={isLoading}
                color="primary"
                variant="text"
              />
            ) : null}
            {isSaveAllowed ? (
              <SpinnerButton
                label={t("common:save")}
                onClick={onSave}
                variant="outlined"
                isLoading={isLoading}
                color="primary"
                sx={{ marginLeft: 2, height: "36px", borderRadius: "20px" }}
                spinnerSx={{ color: theme.palette.primary.main }}
              />
            ) : null}
            {isSaveAllowed ? (
              <ActionButton variant="contained" onClick={confirmAcceptAndSend}>
                {isMultipleInvoices
                  ? t("acknowledgeAndSendInvoices")
                  : t("acceptAndSend")}
              </ActionButton>
            ) : null}
          </Box>
        </>
      ) : (
        <Box />
      )}
      <InvoiceActionBannerPagingControl
        currentInvoice={currentInvoice}
        numberOfInvoices={numberOfInvoices}
        cycleInvoices={cycleInvoices}
      />
      <ConfirmationDialog
        title={confirmationContent.title}
        contentText={confirmationContent.contentText}
        cancelText={confirmationContent.cancelText}
        confirmText={confirmationContent.confirmText}
        isOpen={isConfirmationDialogOpen}
        onConfirm={confirmationContent.confirmAction}
        onCancel={() => setIsConfirmationDialogOpen(false)}
      />
    </ActionBanner>
  );
};
